// DO NOT IMPORT THIS FILE DIRECTLY
// import FirebaseService into your constructors
// otherwise your tests will fail

import { Injectable, inject } from '@angular/core';
import { CopyOverridesInterface, FirebaseService, RemoteConfig_DesktopNativeMapping } from './firebase.service';
import { AmplitudeService } from './amplitude.service';
import { Environment } from '@brightside-web/micro/core/environment';
import { BsCacheService } from '@brightside-web/desktop/data-access/core-services';

import { initializeApp, FirebaseApp } from 'firebase/app';
import {
  RemoteConfig,
  getRemoteConfig,
  fetchAndActivate,
  getValue,
  ensureInitialized,
  fetchConfig,
  onConfigUpdate,
  activate,
} from 'firebase/remote-config';
import { Analytics as FbAnalytics, getAnalytics, setUserProperties, setUserId, logEvent } from 'firebase/analytics';
import * as Sentry from '@sentry/angular';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { filter, first, map } from 'rxjs/operators';

const defaultMapping = {
  'partner/Credit_Card_Refi': {
    path: 'partner/Credit_Card_Refi',
  },
  'partner/Emergency_Cash': {
    path: 'partner/Emergency_Cash',
  },
  'finsol/deep/savings/autosave/once': {
    path: 'home/deep/savings/intro',
    queryParamsType: 'auto',
  },
  home: {
    path: 'home',
  },
  onboarding: {
    path: 'onboarding',
  },
  setupcredit: {
    path: 'credit-setup/creation',
  },
};

export function parseRemoteConfigArray<T>(value: string | null | undefined): T[] {
  if (!value) return [];

  try {
    const firstParse = JSON.parse(value);

    // Case 1: Already a valid array
    if (Array.isArray(firstParse)) return firstParse;

    // Case 2: It's a string that contains JSON (double-encoded)
    if (typeof firstParse === 'string') {
      const secondParse = JSON.parse(firstParse);
      if (Array.isArray(secondParse)) return secondParse;
    }

    return [];
  } catch (e) {
    return [];
  }
}


const pageShownAppend = ['video tapped', 'logo tapped'];

interface ThrottledEvent {
  lastCall: number;
  throttleTime: number;
}

/**
 * this class is poorly named, it sets up both Firebase Analytics AND Firebase Remote Config
 */
@Injectable({
  providedIn: 'root',
})
export class FirebaseAnalyticsService implements FirebaseService {
  private _remoteConfigReady = new BehaviorSubject<boolean>(false);
  remoteConfigReady$ = this._remoteConfigReady.asObservable();
  get remoteConfigReadyPromise(): Promise<void> {
    return firstValueFrom(
      this.remoteConfigReady$.pipe(
        filter((ready) => ready === true),
        first(),
        map(() => {
          return undefined;
        })
      )
    );
  };

  firebaseApp: FirebaseApp;
  fbRemoteConfig: RemoteConfig;

  fbAnalytics: FbAnalytics;

  private _remoteConfigUnsubscribe?: () => void;

  events: { [p: string]: ThrottledEvent } = {};
  lastPageShownEvent: string;

  typeOveride: { [p: string]: () => Promise<string> } = {
    credit: async () => ((await this._bsCacheService.getItem('ONBOARDING_FLOW_ACTIVE')) ? 'onboarding' : 'standalone'),
    'onboarding credit': async () =>
      (await this._bsCacheService.getItem('ONBOARDING_FLOW_ACTIVE')) ? 'onboarding' : 'standalone',
  };

  typeOverRideKeys: string[];
  _guid: string;

  protected _bsCacheService: BsCacheService = inject(BsCacheService);


  constructor(public amplitudeService: AmplitudeService, public env: Environment) {
    this.typeOverRideKeys = Object.keys(this.typeOveride);
    this.firebaseApp = this.initializeApp();
    this.fbRemoteConfig = this.getRemoteConfig();
    this.fbAnalytics = this.getAnalytics();
  }

  initializeApp() {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return initializeApp(this.env.firebase);
  }

  getRemoteConfig() {
    return getRemoteConfig(this.firebaseApp);
  }

  getAnalytics() {
    return getAnalytics(this.firebaseApp);
  }

  async logEvent(
    eventName: string,
    eventParams?: { custom: { [p: string]: any } | string; [p: string]: any },
    shouldThrottle: boolean = false,
    throttleTime?: number
  ): Promise<void> {
    if (eventName && eventName !== 'onboardingcredit shown') {
      if (eventName.includes('shown')) this.lastPageShownEvent = eventName.split('shown')[0].trim();
      if (pageShownAppend.includes(eventName)) eventName = `${this.lastPageShownEvent} ${eventName}`;

      const isEventThrottled = shouldThrottle === false ? false : this.isEventThrottled(eventName, throttleTime);

      if (!isEventThrottled) {
        let params = eventParams ?? {};

        if (eventParams?.custom && typeof eventParams?.custom === 'object') {
          params = eventParams.custom;
        }

        for (let i = 0; i < this.typeOverRideKeys.length; i++) {
          if (eventName.startsWith(this.typeOverRideKeys[i])) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            params['type'] = await this.typeOveride[this.typeOverRideKeys[i]]();
            break;
          }
        }

        if (eventName.split(' ').join('_') !== 'api_call_completed' &&
            eventName.split(' ').join('_') !== 'api_call_started') {
          this.amplitudeService.logEvent(eventName.split('_').join(' '), params);
        }
        Sentry.logger.info(eventName.split(' ').join('_'), {...params, guid: this._guid ?? ''});
        logEvent(this.fbAnalytics, eventName, eventParams);
        if (!this.env.production) {
          console.log(`Analytics event fired: ${eventName.split('_').join(' ')} with `, params);
        }
      }
    }
  }

  isEventThrottled(eventName: string, throttleTime?: number): boolean {
    const event = this.events[eventName];
    const currentCall = Date.now();

    if (event) {
      if (currentCall - event.lastCall > event.throttleTime) {
        event.lastCall = currentCall;
        return false;
      } else {
        return true;
      }
    } else {
      this.events[eventName] = {
        lastCall: currentCall,
        throttleTime: throttleTime ? throttleTime : 1000,
      };
      return false;
    }
  }

  setUserId(id: string, options?: any): void {
    this._guid = id;
    setUserId(this.fbAnalytics, id, options);
    this.amplitudeService.setUserId(id);
  }

  clearProperties(): void {
    setUserId(this.fbAnalytics, '');
    setUserProperties(this.fbAnalytics, {});
    void this.refreshRemoteConfigForUserContext().finally();
  }

  setProperties(properties: { [key: string]: string }) {
    setUserProperties(this.fbAnalytics, properties);
    void this.refreshRemoteConfigForUserContext().finally();
  }

  async refreshRemoteConfigForUserContext(): Promise<void> {
    try {
      await fetchAndActivate(this.fbRemoteConfig);
      this._remoteConfigReady.next(true);
    } catch (e) {
      Sentry.captureException(e);
    }
  }

  setupRemoteConfig() {
    fetchAndActivate(this.fbRemoteConfig)
      .then(() => {
      
        // First time we’re sure values are available
        this._remoteConfigReady.next(true);

        // Start realtime listener once we have an initial template
        this._startRealtimeRemoteConfigListener();
      })
      .catch((error) => {
        Sentry.captureException(error);
      });
  }

  private _startRealtimeRemoteConfigListener(): void {
    // Avoid double-subscribing if setupRemoteConfig() is called more than once
    if (this._remoteConfigUnsubscribe) {
      return;
    }

    this._remoteConfigUnsubscribe = onConfigUpdate(this.fbRemoteConfig, {
      next: async (configUpdate) => {
      
        try {
          const updatedKeys = configUpdate.getUpdatedKeys();
          if (!this.env.production) {
            console.log('[RC] Updated keys:', Array.from(updatedKeys));
          }
          await activate(this.fbRemoteConfig);
          this._remoteConfigReady.next(true);
        } catch (err) {
          Sentry.captureException(err);
        }
      },
      error: (error: unknown) => {
        // Stream connection errors are common when backend is unreachable (firewall, offline).
        // App already has config from fetchAndActivate(); avoid sending to Sentry.
        const code = (error as { code?: string })?.code;
        const message = (error as { message?: string })?.message ?? '';
        const isStreamError =
          code === 'remoteconfig/stream-error' ||
          (typeof message === 'string' &&
            (message.includes('remoteconfig/stream-error') || message.includes('stream was not able to connect')));
        if (!isStreamError) {
          Sentry.captureException(error);
        }
        if (!this.env.production) {
          console.warn('[RC] onConfigUpdate error (realtime updates may be unavailable)', error);
        }
      },
      complete: () => {
        // This is called if the listener is stopped for good
        if (!this.env.production) {
          console.log('[RC] Realtime Remote Config listening stopped.');
        }
        this._remoteConfigUnsubscribe = undefined;
      },
    });
  }


  remoteConfig(): RemoteConfig {
    return this.fbRemoteConfig;
  }

  getValueAsString(valueKey: string): string {
    return getValue(this.fbRemoteConfig, valueKey).asString();
  }

  getValueAsBoolean(valueKey: string): boolean {
    return getValue(this.fbRemoteConfig, valueKey).asBoolean();
  }


  getValueAsArray<T>(valueKey: string): T[] {
    return parseRemoteConfigArray<T>(this.getValueAsString(valueKey));
  }

  ensureInitialized(): Promise<void> {
    return ensureInitialized(this.fbRemoteConfig);
  }

  fetchConfig(): Promise<void> {
    return fetchConfig(this.fbRemoteConfig);
  }

  remoteConfigMapping(): { [key: string]: any } {
    try {
      return JSON.parse(getValue(this.fbRemoteConfig, RemoteConfig_DesktopNativeMapping).asString()) as { [key: string]: any };
    } catch (e) {
      return defaultMapping;
    }
    return defaultMapping;
  }
  copyOverridesMapping() : CopyOverridesInterface[] | [] {
    try {
      return JSON.parse(getValue(this.fbRemoteConfig, 'copyOverride').asString()) as CopyOverridesInterface[] ;
    } catch (e) {
      return [];
    }
  }
}
